import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gift } from "lucide-react";

import { LegalFooter } from "../components/chalk/LegalLayout";
import { EARLY_ACCESS_NOTE, usePaymentsOpen } from "../lib/billingStatus";
import {
  DEMO_TOURNAMENT_SLUG, FAQ, FEATURES, PRICE_ROWS, RunCta, STEPS,
} from "../components/chalk/TournamentMarketing";
import { BODY, C, DISPLAY, MARKER, SectionHead, TopBar, cardBodyStyle, cardLight, cardTitleStyle, sectionBase } from "./Landing";

/** Public marketing page for Tournament Mode: /tournaments */
export default function TournamentsLanding() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Chalk OS Tournaments: run your tournament from your phone";
    return () => { document.title = prev; };
  }, []);
  return (
    <div data-testid="tournaments-landing" style={{ fontFamily: BODY, color: C.textDark, background: C.cream }}>
      <TopBar />
      <main>
        <Hero />
        <Features />
        <Steps />
        <Prices />
        <Faq />
        <Final />
      </main>
      <LegalFooter />
    </div>
  );
}

function Hero() {
  return (
    <section className="chalkboard-texture" style={{ background: C.greenDark, color: C.chalkWhite, padding: "72px 22px 80px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ fontFamily: BODY, fontWeight: 700, fontSize: 12, letterSpacing: "0.16em", color: C.greenChalk }}>CHALK OS · TOURNAMENT MODE</div>
        <h1 data-testid="tl-title" style={{ fontFamily: DISPLAY, fontWeight: 400, fontSize: "clamp(3rem, 9vw, 6rem)", lineHeight: 0.92, margin: "12px 0 0", textTransform: "uppercase" }}>
          Run your tournament from your phone.</h1>
        <div style={{ fontFamily: MARKER, fontSize: "clamp(1.5rem, 4vw, 2.4rem)", color: C.greenChalk, marginTop: 10 }}>No app for players or fans.</div>
        <p style={{ fontSize: 18, lineHeight: 1.55, color: C.chalkMuted, maxWidth: 600, marginTop: 18 }}>
          Live brackets, check-in, announcements and a Big Screen for the venue TV. Everyone follows with one link.</p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28, alignItems: "center" }}>
          <RunCta testId="tl-hero-cta" dark={false} />
          <a href={`/t/${DEMO_TOURNAMENT_SLUG}`} target="_blank" rel="noreferrer" data-testid="tl-demo-link"
             style={{ color: C.chalkWhite, fontWeight: 700, fontSize: 14, textDecoration: "underline", textUnderlineOffset: 4 }}>See a live demo tournament</a>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section style={{ ...sectionBase, background: C.offWhite }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <SectionHead title="Everything on game day." subtitle="In your pocket." />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 40 }}>
          {FEATURES.map(({ Icon, name, body }) => (
            <div key={name} className="chalk-marketing-card" style={cardLight} data-testid={`tl-feature-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Icon size={26} color={C.greenMid} aria-hidden="true" />
              <div style={cardTitleStyle}>{name}</div>
              <p style={cardBodyStyle}>{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Steps() {
  return (
    <section style={{ ...sectionBase, background: C.cream }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <SectionHead title="How it works." subtitle="Three steps." />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, marginTop: 40 }}>
          {STEPS.map((s) => (
            <div key={s.n} className="chalk-marketing-card" style={cardLight} data-testid={`tl-step-${s.n}`}>
              <div style={{ fontFamily: DISPLAY, fontSize: 48, color: C.greenMid, lineHeight: 0.9 }}>{s.n}</div>
              <div style={{ ...cardTitleStyle, marginTop: 10 }}>{s.title}</div>
              <p style={cardBodyStyle}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Prices() {
  const open = usePaymentsOpen();
  return (
    <section id="tournament-pricing" style={{ ...sectionBase, background: C.offWhite }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <SectionHead title="Pricing." subtitle="Pay per event, or by the year." />
        <div data-testid="tl-first-free" style={{ display: "flex", gap: 12, alignItems: "center", background: "#1f3b2d", color: C.chalkWhite,
                                                  borderRadius: 14, padding: "16px 18px", marginTop: 36 }}>
          <Gift size={26} color={C.greenChalk} aria-hidden="true" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontFamily: DISPLAY, fontSize: 26, letterSpacing: "0.02em", lineHeight: 1 }}>FIRST HUB EVENT FREE</div>
            <div style={{ fontSize: 14, color: C.chalkMuted, marginTop: 4 }}>Up to 32 teams, one per director. Card on file, nothing charged today.</div>
          </div>
        </div>
        {!open && <div data-testid="tl-early-access" style={{ fontWeight: 700, fontSize: 14, color: C.greenMid, marginTop: 14 }}>{EARLY_ACCESS_NOTE}</div>}
        <div data-testid="tl-price-table" style={{ ...cardLight, padding: 0, marginTop: 14, overflow: "hidden" }}>
          {PRICE_ROWS.map(([plan, price, detail], i) => (
            <div key={i} data-testid={`tl-price-row-${i}`} style={{ padding: "14px 18px", borderTop: i ? `1px solid ${C.border}` : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                <div style={{ fontWeight: 700 }}>{plan}</div>
                <div style={{ fontFamily: DISPLAY, fontSize: 24, letterSpacing: "0.02em", whiteSpace: "nowrap" }}>{price}</div>
              </div>
              <div style={{ fontSize: 14, color: C.muted, marginTop: 2 }}>{detail}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 13, color: C.muted, marginTop: 12, lineHeight: 1.5 }}>
          Hub pricing is by teams that check in or play. Pay for today's count when you upgrade; any difference is settled after the event.{" "}
          <Link to="/terms#tournament-billing" data-testid="tl-terms-link" style={{ color: C.greenMid, fontWeight: 700 }}>Pricing terms</Link></p>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section style={{ ...sectionBase, background: C.cream }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <SectionHead title="Questions." />
        <div style={{ marginTop: 32 }}>
          {FAQ.map(([q, a], i) => (
            <details key={q} data-testid={`tl-faq-${i}`} open={i < 3} style={{ ...cardLight, marginBottom: 10 }}>
              <summary style={{ fontWeight: 800, fontSize: 17, cursor: "pointer" }}>{q}</summary>
              <p style={{ ...cardBodyStyle, fontSize: 15 }}>{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Final() {
  return (
    <section className="chalkboard-texture" style={{ ...sectionBase, background: C.greenDark, textAlign: "center" }}>
      <SectionHead title="Your next tournament, handled." dark />
      <div style={{ marginTop: 28, display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
        <RunCta testId="tl-final-cta" dark={false} />
        <a href={`/t/${DEMO_TOURNAMENT_SLUG}`} target="_blank" rel="noreferrer" data-testid="tl-final-demo"
           style={{ color: C.greenChalk, fontWeight: 700, display: "inline-flex", gap: 6, alignItems: "center", textDecoration: "none" }}>
          Live demo <ArrowRight size={14} aria-hidden="true" /></a>
      </div>
    </section>
  );
}
